// frontend/app/login/page.tsx

"use client";

import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "@/i18n/useTranslation";
import toast from "react-hot-toast";

function LoginContent() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const { refreshUser } = useAuth();
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const usePassword = true;
    const [rememberMe, setRememberMe] = useState(false);

    // Estado para email no verificado
    const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
    const [unverifiedEmail, setUnverifiedEmail] = useState("");
    const [resendLoading, setResendLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    // Mostrar toast si viene de verificación exitosa
    useEffect(() => {
        if (searchParams.get("verified") === "true") {
            toast.success(t("login.emailVerified") || "¡Email verificado! Ya puedes iniciar sesión");
            router.replace("/login", { scroll: false });
        }
    }, [searchParams, router, t]);

    // Cooldown para reenvío
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    async function handleLogin(e: React.FormEvent) {
        e.preventDefault();
        setError("");
        setShowVerificationPrompt(false);
        setLoading(true);

        refreshUser();

        try {
            const endpoint = usePassword ? "/api/auth/login-password" : "/api/auth/login";
            const body = usePassword
                ? JSON.stringify({ email, password })
                : JSON.stringify({ email });

            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body,
            });

            const data = await res.json();

            if (!res.ok) {
                if (res.status === 403 && data.detail === "email_not_verified") {
                    setUnverifiedEmail(email);
                    setShowVerificationPrompt(true);
                    setError(t("login.emailNotVerified") || "Debes verificar tu email antes de iniciar sesión");
                    return;
                }

                setError(data.detail || t("login.invalidCredentials"));
                return;
            }

            await new Promise(resolve => setTimeout(resolve, 300));
            await refreshUser();
            window.location.href = "/reservas";
        } catch (err) {
            setError(t("errors.networkError"));
        } finally {
            setLoading(false);
        }
    }

    async function handleResendVerification() {
        if (resendLoading || resendCooldown > 0 || !unverifiedEmail) return;

        setResendLoading(true);
        try {
            const res = await fetch("/api/auth/resend-verification", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: unverifiedEmail }),
            });

            if (res.ok) {
                toast.success(t("login.verificationResent") || "Email de verificación reenviado");
                setResendCooldown(60);
            } else {
                toast.error(t("errors.generic") || "Error al reenviar");
            }
        } catch (err) {
            toast.error(t("errors.networkError"));
        } finally {
            setResendLoading(false);
        }
    }

    return (
        <main className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#0A0A0B' }}>
            <div className="w-full max-w-sm rounded-xl p-6" style={{ backgroundColor: '#141416' }}>

                {/* Logo Rezvio */}
                <div className="flex justify-center mb-8">
                    <Image
                        src="/images/logo.svg"
                        alt="Rezvio"
                        width={140}
                        height={40}
                        priority
                    />
                </div>

                <h1 className="text-xl font-medium mb-6 text-center text-white">
                    {t("login.title")}
                </h1>

                <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                        <label className="block text-sm text-zinc-400 mb-1">
                            {t("login.email")}
                        </label>
                        <input
                            type="email"
                            name="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setShowVerificationPrompt(false);
                            }}
                            required
                            className="input"
                        />
                    </div>

                    {usePassword && (
                        <div>
                            <label className="block text-sm text-zinc-400 mb-1">
                                {t("login.password")}
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="input w-full pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200 text-sm"
                                    tabIndex={-1}
                                >
                                    {showPassword ? t("login.hide_password") : t("login.show_password")}
                                </button>
                            </div>
                        </div>
                    )}

                    {usePassword && (
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="rememberMe"
                                name="rememberMe"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 accent-[#00D4AA] cursor-pointer"
                            />
                            <label htmlFor="rememberMe" className="text-sm text-zinc-400 cursor-pointer select-none">
                                {t("login.rememberMe")}
                            </label>
                        </div>
                    )}

                    {error && (
                        <div className="text-red-400 text-sm text-center">
                            {error}
                        </div>
                    )}

                    {showVerificationPrompt && (
                        <div className="bg-amber-900/30 border border-amber-700 rounded p-3 text-center">
                            <p className="text-amber-300 text-sm mb-2">
                                {t("login.verificationRequired") || "Revisa tu email y haz clic en el enlace de verificación"}
                            </p>
                            {resendCooldown > 0 ? (
                                <p className="text-[#00D4AA] text-xs">
                                    ✓ {t("register.emailResent") || "Email reenviado"} ({resendCooldown}s)
                                </p>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleResendVerification}
                                    disabled={resendLoading}
                                    className="text-[#00D4AA] hover:text-[#00E8BB] text-sm font-medium disabled:opacity-50"
                                >
                                    {resendLoading
                                        ? (t("common.sending") || "Enviando...")
                                        : (t("login.resendVerification") || "Reenviar email de verificación")
                                    }
                                </button>
                            )}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-2.5 rounded-lg font-medium disabled:opacity-50 transition-colors"
                        style={{
                            backgroundColor: '#00D4AA',
                            color: '#0A0A0B'
                        }}
                    >
                        {loading ? t("login.loggingIn") : t("login.submit")}
                    </button>
                </form>

                {usePassword && (
                    <div className="mt-4 text-center">
                        <Link
                            href="/forgot-password"
                            className="text-sm text-zinc-400 hover:text-[#00D4AA]"
                        >
                            {t("login.forgotPassword")}
                        </Link>
                    </div>
                )}

                <div className="mt-6 pt-4 border-t border-zinc-800">
                    <div className="text-center">
                        <span className="text-zinc-500 text-xs block mb-1">{t("register.noAccount")}</span>
                        <Link href="/register" className="text-[#00D4AA] hover:text-[#00E8BB] text-sm font-medium">
                            {t("register.createAccount")}
                        </Link>
                    </div>
                </div>
            </div>
        </main>
    );
}

export default function LoginPage() {
    return (
        <Suspense fallback={
            <main className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A0A0B' }}>
                <div className="text-zinc-400">Cargando...</div>
            </main>
        }>
            <LoginContent />
        </Suspense>
    );
}